import { useState, useEffect } from "react";
import { Save, Loader2, CheckCircle, AlertTriangle, ShieldCheck, Terminal, HelpCircle } from "lucide-react";
import { db } from "@/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminGeneral() {
  const { user } = useAuth();
  const [gtmId, setGtmId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" }); // { type: 'success' | 'error' | null, message: '' }

  // Expresión regular para validar el formato oficial de GTM (ej. GTM-XXXXXXX)
  const GTM_REGEX = /^GTM-[A-Z0-9]{4,12}$/i;

  useEffect(() => {
    async function loadConfig() {
      setLoading(true);
      try {
        const docSnap = await getDoc(doc(db, "config", "general"));
        if (docSnap.exists()) {
          setGtmId(docSnap.data().gtmId || "");
        }
      } catch (error) {
        console.error("Error al cargar la configuración general:", error);
        setStatus({
          type: "error",
          message: "No se pudo cargar la configuración desde la base de datos.",
        });
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);

  const handleInputChange = (e) => {
    // Convertir automáticamente a mayúsculas y limpiar espacios en blanco
    setGtmId(e.target.value.toUpperCase().trim());
    if (status.type) {
      setStatus({ type: null, message: "" });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Permitir campo vacío (para deshabilitar GTM) o validar formato si tiene texto
    if (gtmId && !GTM_REGEX.test(gtmId)) {
      setStatus({
        type: "error",
        message: "El formato del ID de GTM es inválido. Debe comenzar con 'GTM-' seguido de letras y números.",
      });
      return;
    }

    setSaving(true);
    setStatus({ type: null, message: "" });

    try {
      await setDoc(
        doc(db, "config", "general"),
        {
          gtmId: gtmId || null,
          updatedAt: new Date().toISOString(),
          updatedBy: user?.uid || "admin",
        },
        { merge: true }
      );

      setStatus({
        type: "success",
        message: gtmId
          ? `Configuración guardada. El contenedor ${gtmId} está activo.`
          : "Configuración guardada. Google Tag Manager ha sido desactivado.",
      });
    } catch (error) {
      console.error("Error al guardar la configuración:", error);
      setStatus({
        type: "error",
        message: "Hubo un error al intentar guardar los cambios en Firestore.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin" size={32} style={{ color: "#00628F" }} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Tarjeta de Configuración de Google Tag Manager */}
      <div
        className="rounded-2xl border p-6 transition-all duration-300"
        style={{
          background: "white",
          borderColor: "#E2E8F0",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.025)",
        }}
      >
        <div className="flex items-start gap-4 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "#E0F2FE" }}
          >
            <Terminal size={20} style={{ color: "#0284C7" }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-800">Google Tag Manager</h3>
            <p className="text-xs text-slate-500 mt-1">
              Administra el tag global de seguimiento y análisis de la plataforma.
            </p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Feedback Banners */}
          {status.type === "success" && (
            <div
              className="flex items-start gap-3 p-4 rounded-xl border animate-fade-in"
              style={{ background: "#F0FDF4", borderColor: "#BBF7D0", color: "#166534" }}
            >
              <CheckCircle className="shrink-0 mt-0.5" size={18} />
              <div className="text-xs font-medium">{status.message}</div>
            </div>
          )}

          {status.type === "error" && (
            <div
              className="flex items-start gap-3 p-4 rounded-xl border animate-fade-in"
              style={{ background: "#FEF2F2", borderColor: "#FECACA", color: "#991B1B" }}
            >
              <AlertTriangle className="shrink-0 mt-0.5" size={18} />
              <div className="text-xs font-medium">{status.message}</div>
            </div>
          )}

          {/* Input field */}
          <div className="space-y-2">
            <label htmlFor="gtm-id-input" className="block text-xs font-bold text-slate-700">
              ID del Contenedor de GTM
            </label>
            <div className="relative max-w-md">
              <input
                id="gtm-id-input"
                type="text"
                placeholder="GTM-XXXXXXX"
                value={gtmId}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-xl border text-sm font-medium tracking-wide focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: "#CBD5E1",
                  background: "#F8FAFC",
                  color: "#1E293B",
                }}
                disabled={saving}
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1.5">
              <HelpCircle size={12} />
              Deja este campo en blanco para deshabilitar por completo el rastreo de Google Tag Manager.
            </p>
          </div>

          <div style={{ borderTop: "1px solid #F1F5F9" }} className="pt-6 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm transition-all hover:opacity-95 active:scale-95 disabled:opacity-50 cursor-pointer"
              style={{ background: "linear-gradient(135deg, #00628F 0%, #2A9D8F 100%)" }}
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Tarjeta de Ayuda y Contexto Técnico */}
      <div
        className="rounded-2xl border p-6"
        style={{
          background: "#F8FAFC",
          borderColor: "#E2E8F0",
        }}
      >
        <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center gap-2">
          <ShieldCheck size={14} style={{ color: "#00628F" }} />
          ¿Cómo funciona esta integración?
        </h4>
        <ul className="space-y-2.5 text-xs text-slate-600 pl-1">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
            <span>
              <strong>Carga en Caliente:</strong> Cuando se define un ID de GTM válido, la plataforma descarga e inyecta dinámicamente el código oficial de Google al inicializarse en el navegador de cualquier usuario.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
            <span>
              <strong>Soporte para Rutas Virtuales (SPA):</strong> Debido a que el sistema funciona sin recargas de página físicas, la plataforma envía un evento sintético de <code>page_view</code> a la cola de GTM (<code>window.dataLayer</code>) en cada cambio de sección (ej. al ir al perfil, dashboard o realizar una medición).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
            <span>
              <strong>Control de Privacidad:</strong> Al borrar el ID de GTM y guardar los cambios, se bloqueará la descarga del script de rastreo en el siguiente inicio de sesión, respetando las políticas de consentimiento.
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
